import { get, isInteger, isNaN, isNil, parseInt } from 'lodash';
import { MAX_LOCATION_DISTANCE } from '@nandosaus/constants';
import { observer } from 'mobx-react';
import { useRootStore } from '@nandosaus/state-management';
import { useRouter } from 'next/router';
import React, { useEffect } from 'react';
import styled from 'styled-components';

import { Box, Col, Container, Row } from '../../components/grid';
import { DefaultState } from '../../components/default';
import { H1 } from '../../components/typography';
import { Layout } from '../../components/layout';
import { LoadingIndicator } from '../../components/icons/loading-indicator';
import { navigation } from '../../constants';
import { RestaurantMap } from '../../components/restaurant-map';
import { RestaurantsList } from '../../components/restaurant-list';
import { useIsMinWidth } from '../../hooks/use-is-min-width';

const MAX_NUMBER_OF_RESTAURANTS = 10;
const validateCoordinateParam = queryParam => !isNil(queryParam) && !isNaN(parseFloat(queryParam));

const RestaurantsStatePage = observer(() => {
  const { RestaurantStore } = useRootStore();
  const isMobile = !useIsMinWidth('md');
  const router = useRouter();

  const queryLatitude = get(router, 'query.lat');
  const queryLongitude = get(router, 'query.lng');
  const queryDisplayRestaurant = get(router, 'query.displayRestaurant');
  const displayRestaurant = parseInt(queryDisplayRestaurant);

  const isValidLatitude = validateCoordinateParam(queryLatitude);
  const isValidLongitude = validateCoordinateParam(queryLongitude);
  const isValidDisplayRestaurant = !!(isInteger(displayRestaurant) && displayRestaurant > 0);

  /**
   * Preload all restaurants when the page is loaded.
   */
  useEffect(() => {
    if (!RestaurantStore.loaded) {
      RestaurantStore.loadRestaurants();
    }
  }, [RestaurantStore]);

  /**
   * Redirect to the restaurants page if lat/lng are not valid
   */
  useEffect(() => {
    if (router.isReady && (!isValidLatitude || !isValidLongitude)) {
      router.push(`${navigation.RESTAURANTS}`);
    }
  }, [isValidLatitude, isValidLongitude, router, router.isReady]);

  const searchCoordinates = { latitude: parseFloat(queryLatitude), longitude: parseFloat(queryLongitude) };
  const closestRestaurants = RestaurantStore.closestRestaurants(searchCoordinates, MAX_NUMBER_OF_RESTAURANTS).map(
    result => result.restaurant
  );

  const closestLimitedRestaurants = closestRestaurants.slice(1, displayRestaurant + 1);

  const restaurantsFound = isValidDisplayRestaurant
    ? closestLimitedRestaurants.length > 0
    : closestRestaurants.length > 0;

  return (
    <Layout title="Closest Restaurants">
      <Container flexDirection="column" mt="2rem" flex={1}>
        <H1 mb="3rem" textAlign="center">
          Closest Restaurants
        </H1>
        {RestaurantStore.loaded ? (
          <Box>
            {restaurantsFound ? (
              <Row flexWrap="wrap" flexDirection={{ xs: 'column-reverse', lg: 'row' }}>
                <Col width={{ xs: 12 / 12, lg: 5 / 12 }}>
                  {closestRestaurants && (
                    <RestaurantsList
                      variant="card"
                      coordinates={searchCoordinates}
                      restaurants={isValidDisplayRestaurant ? closestLimitedRestaurants : closestRestaurants}
                      showRestaurantStatus
                    />
                  )}
                </Col>
                <Col position="relative" width={{ xs: 12 / 12, lg: 7 / 12 }} mb={{ xs: 2, lg: 0 }}>
                  <MapWrapper bg="white" p="0.25rem">
                    <RestaurantMap
                      height={isMobile ? '500px' : '629px'}
                      coordinates={searchCoordinates}
                      linkText="View restaurant info"
                      restaurants={isValidDisplayRestaurant ? closestLimitedRestaurants : closestRestaurants}
                      showRestaurantStatus
                    />
                  </MapWrapper>
                </Col>
              </Row>
            ) : (
              <Box textAlign="center">
                <DefaultState
                  subtitle={`Unfortunately we don’t have a restaurant within ${MAX_LOCATION_DISTANCE /
                    1000}km of your search.`}
                  title="No results"
                />
              </Box>
            )}
          </Box>
        ) : (
          <LoadingIndicator />
        )}
      </Container>
    </Layout>
  );
});

const MapWrapper = styled(Box)`
  position: sticky;
  top: 1rem;
`;

export default RestaurantsStatePage;
